import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  ChakraProvider,
  Container,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  HStack,
  Input,
  Textarea,
  VStack,
  useToast,
  extendTheme,
  Card,
  CardBody,
  CardHeader,
  Image,
  Text,
  Select
} from '@chakra-ui/react';
import { FaSave, FaUndo } from 'react-icons/fa';

const theme = extendTheme({
  colors: {
    brand: {
      50: '#e3f2fd',
      100: '#bbdefb',
      200: '#90caf9',
      300: '#64b5f6',
      400: '#42a5f5',
      500: '#2196F3',
      600: '#1976D2',
      700: '#1565C0',
    },
  },
});

// Country list - GCC & MENA countries plus Pakistan, India, and Sri Lanka
const countries = [
  // GCC Countries
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', phoneCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', phoneCode: '+966' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', phoneCode: '+965' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', phoneCode: '+974' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', phoneCode: '+973' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', phoneCode: '+968' },
  // MENA Countries
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', phoneCode: '+20' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴', phoneCode: '+962' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧', phoneCode: '+961' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶', phoneCode: '+964' },
  { code: 'SY', name: 'Syria', flag: '🇸🇾', phoneCode: '+963' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪', phoneCode: '+967' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸', phoneCode: '+970' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', phoneCode: '+212' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳', phoneCode: '+216' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿', phoneCode: '+213' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾', phoneCode: '+218' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩', phoneCode: '+249' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', phoneCode: '+90' },
  // Additional Countries
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', phoneCode: '+92' },
  { code: 'IN', name: 'India', flag: '🇮🇳', phoneCode: '+91' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', phoneCode: '+94' },
];

const BrandOnboardingForm = ({ platform }) => {
  const [formData, setFormData] = useState({
    brandName: '',
    contactPersonName: '',
    email: '',
    tradeLicenseNumber: '',
    taxId: '',
    address: '',
    city: '',
    poBox: '',
    country: 'United Arab Emirates',
    phoneCountryCode: '+971',
    phoneNumber: '',
    bankName: '',
    accountNumber: '',
    iban: '',
    swiftCode: '',
    accountHolderName: '',
    bankAccountCurrency: 'AED',
    bankAccountCurrencyOther: ''
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [files, setFiles] = useState({
    tradeLicenseCopy: null,
    brandLogo: null
  });
  const toast = useToast();

  // Brand Onboarding Script URL - WORKING SCRIPT WITH TIMESTAMPS
  // TODO: Deploy BRAND_ONBOARDING_WORKING_SCRIPT.js and update this URL
  const scriptUrl = 'https://script.google.com/macros/s/AKfycbwgtEF473Dl0m6jL1ojAXljEneie2dL7EnnP5NI0JP9wvKh6S_wXjVvF02e6AoL57hp3A/exec';

  // Validate required fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Brand name is required';
    }
    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.tradeLicenseNumber.trim()) {
      newErrors.tradeLicenseNumber = 'Trade license number is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    }
    if (!formData.iban.trim()) {
      newErrors.iban = 'IBAN is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save to localStorage
  const saveToLocalStorage = useCallback(() => {
    const dataToSave = {
      formData,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('brandOnboardingData', JSON.stringify(dataToSave));
  }, [formData]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem('brandOnboardingData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.formData) setFormData(parsed.formData);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // Auto-save to localStorage when data changes
  useEffect(() => {
    const timer = setTimeout(() => {
      saveToLocalStorage();
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, saveToLocalStorage]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field] && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileUpload = async (field, event) => {
    const file = event.target.files[0];
    if (file) {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1]; // Remove data:image/png;base64, prefix
        setFiles(prev => ({
          ...prev,
          [field]: {
            name: file.name,
            type: file.type,
            size: file.size,
            base64: base64String
          }
        }));
        toast({
          title: 'File uploaded',
          description: `${file.name} has been selected`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      };
      reader.onerror = () => {
        toast({
          title: 'Error',
          description: 'Failed to read file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Reset form to empty state
  const resetForm = () => {
    // Reset form data to initial state
    setFormData({
      brandName: '',
      contactPersonName: '',
      email: '',
      tradeLicenseNumber: '',
      taxId: '',
      address: '',
      city: '',
      poBox: '',
      country: 'United Arab Emirates',
      phoneCountryCode: '+971',
      phoneNumber: '',
      bankName: '',
      accountNumber: '',
      iban: '',
      swiftCode: '',
      accountHolderName: '',
      bankAccountCurrency: 'AED',
      bankAccountCurrencyOther: ''
    });
    
    // Clear files
    setFiles({
      tradeLicenseCopy: null,
      brandLogo: null
    });
    
    // Clear errors
    setErrors({});
    
    // Clear localStorage
    localStorage.removeItem('brandOnboardingData');
    
    // Show success toast
    toast({
      title: 'Form reset',
      description: 'All form data has been cleared',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const saveToGoogleSheets = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields before saving.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      const headers = [
        'Brand Name', 'Contact Person Name',
        'Email', 'Trade License Number', 'Tax ID',
        'Country', 'City', 'P.O. Box', 'Phone Country Code', 'Phone Number', 'Address',
        'Bank Name', 'Account Number', 'IBAN', 'SWIFT Code',
        'Account Holder Name', 'Bank Account Currency'
      ];

      const bankCurrency = formData.bankAccountCurrency === 'Other' 
        ? formData.bankAccountCurrencyOther 
        : formData.bankAccountCurrency;

      const row = [
        formData.brandName,
        formData.contactPersonName,
        formData.email,
        formData.tradeLicenseNumber,
        formData.taxId,
        formData.country,
        formData.city,
        formData.poBox,
        formData.phoneCountryCode,
        formData.phoneNumber,
        formData.address,
        formData.bankName,
        formData.accountNumber,
        formData.iban,
        formData.swiftCode,
        formData.accountHolderName,
        bankCurrency
      ];

      const payload = {
        headers: headers,
        data: [row],
        brandName: formData.brandName.trim(),
        files: {
          tradeLicenseCopy: files.tradeLicenseCopy,
          brandLogo: files.brandLogo
        }
      };

      // Debug logging - MORE DETAILED
      console.log('=== BRAND ONBOARDING DEBUG ===');
      console.log('Headers:', headers);
      console.log('Headers count:', headers.length);
      console.log('Row data:', row);
      console.log('Row length:', row.length);
      console.log('Form data sample:', {
        brandName: formData.brandName,
        contactPersonName: formData.contactPersonName,
        email: formData.email
      });
      console.log('Payload structure:', {
        headersCount: headers.length,
        dataRowsCount: [row].length,
        firstRowLength: row.length,
        brandName: formData.brandName.trim(),
        hasFiles: !!files.tradeLicenseCopy || !!files.brandLogo
      });

      // Use same fetch approach as Product Onboarding
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify(payload)
      });
      
      console.log('Data sent using Product Onboarding approach');

      localStorage.removeItem('brandOnboardingData');

      toast({
        title: 'Data saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Error saving data',
        description: 'Please check your internet connection and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <ChakraProvider theme={theme}>
      <Box minH="100vh" bg="gray.50">
        <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }}>
          {/* Logo */}
          <Box mb={6} textAlign="center">
            <Image 
              src="/logo.svg" 
              alt="Pose Logo" 
              maxH="80px"
              maxW="200px"
              mx="auto"
            />
          </Box>

          <Card p={{ base: 2, md: 0 }}>
            <CardHeader p={{ base: 3, md: 6 }}>
              <VStack spacing={4}>
                <Heading size={{ base: "md", md: "lg" }} color="black" textAlign={{ base: "center", md: "left" }}>
                  Brand Onboarding Checklist
                </Heading>
                <Text color="black" fontSize={{ base: "sm", md: "md" }} textAlign={{ base: "center", md: "left" }}>
                  Complete vendor information form - Fill all required fields and save when complete
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={{ base: 3, md: 6 }}>
              {/* Basic Information */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <Heading size={{ base: "xs", md: "sm" }} color="black">Basic Information</Heading>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.brandName}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Brand Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.brandName}
                          onChange={(e) => handleFormChange('brandName', e.target.value)}
                          placeholder="Enter brand name"
                          bg="white"
                          borderColor={errors.brandName ? "red.500" : "gray.300"}
                        />
                        {errors.brandName && (
                          <FormErrorMessage fontSize="xs">{errors.brandName}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.contactPersonName}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Contact Person Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.contactPersonName}
                          onChange={(e) => handleFormChange('contactPersonName', e.target.value)}
                          placeholder="Enter contact person name"
                          bg="white"
                          borderColor={errors.contactPersonName ? "red.500" : "gray.300"}
                        />
                        {errors.contactPersonName && (
                          <FormErrorMessage fontSize="xs">{errors.contactPersonName}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.email}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Email <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          type="email"
                          size="sm"
                          value={formData.email}
                          onChange={(e) => handleFormChange('email', e.target.value)}
                          placeholder="Enter email address"
                          bg="white"
                          borderColor={errors.email ? "red.500" : "gray.300"}
                        />
                        {errors.email && (
                          <FormErrorMessage fontSize="xs">{errors.email}</FormErrorMessage>
                        )}
                      </FormControl>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Legal Information */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <VStack align="flex-start" spacing={1}>
                    <Heading size={{ base: "xs", md: "sm" }} color="black">Legal Information</Heading>
                    <Text fontSize="xs" color="gray.600" fontWeight="bold">
                      Why we need these: Legal compliance with UAE e-commerce regulations • Process your payments smoothly • Maintain a trusted marketplace
                    </Text>
                  </VStack>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.tradeLicenseNumber}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Trade License Number <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.tradeLicenseNumber}
                          onChange={(e) => handleFormChange('tradeLicenseNumber', e.target.value)}
                          placeholder="Enter trade license number"
                          bg="white"
                          borderColor={errors.tradeLicenseNumber ? "red.500" : "gray.300"}
                        />
                        {errors.tradeLicenseNumber && (
                          <FormErrorMessage fontSize="xs">{errors.tradeLicenseNumber}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Trade License Copy
                        </FormLabel>
                        <Input
                          type="file"
                          size="sm"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload('tradeLicenseCopy', e)}
                          bg="white"
                        />
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Tax ID / VAT Number <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500">(if applicable)</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.taxId}
                          onChange={(e) => handleFormChange('taxId', e.target.value)}
                          placeholder="Enter tax ID or VAT number"
                          bg="white"
                        />
                      </FormControl>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Address & Contact */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <Heading size={{ base: "xs", md: "sm" }} color="black">Address & Contact</Heading>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "200px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Country
                        </FormLabel>
                        <Select
                          size="sm"
                          value={formData.country}
                          onChange={(e) => handleFormChange('country', e.target.value)}
                          placeholder="Select country"
                          bg="white"
                        >
                          {countries.map(country => (
                            <option key={country.code} value={country.name}>
                              {country.flag} {country.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "200px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          City
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.city}
                          onChange={(e) => handleFormChange('city', e.target.value)}
                          placeholder="Enter city"
                          bg="white"
                        />
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "200px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          P.O. Box <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500">(if applicable)</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.poBox}
                          onChange={(e) => handleFormChange('poBox', e.target.value)}
                          placeholder="Enter P.O. Box"
                          bg="white"
                        />
                      </FormControl>
                    </HStack>

                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "120px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Country Code <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Select
                          size="sm"
                          value={formData.phoneCountryCode}
                          onChange={(e) => handleFormChange('phoneCountryCode', e.target.value)}
                          bg="white"
                        >
                          {countries.map(country => (
                            <option key={country.code} value={country.phoneCode}>
                              {country.flag} {country.phoneCode}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "250px" }} isInvalid={!!errors.phoneNumber}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Phone Number <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          type="tel"
                          size="sm"
                          value={formData.phoneNumber}
                          onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                          placeholder="Enter phone number"
                          bg="white"
                          borderColor={errors.phoneNumber ? "red.500" : "gray.300"}
                        />
                        {errors.phoneNumber && (
                          <FormErrorMessage fontSize="xs">{errors.phoneNumber}</FormErrorMessage>
                        )}
                      </FormControl>
                    </HStack>

                    <FormControl isInvalid={!!errors.address}>
                      <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                        Address <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Textarea
                        size="sm"
                        value={formData.address}
                        onChange={(e) => handleFormChange('address', e.target.value)}
                        placeholder="Enter complete address"
                        bg="white"
                        rows={3}
                        borderColor={errors.address ? "red.500" : "gray.300"}
                      />
                      {errors.address && (
                        <FormErrorMessage fontSize="xs">{errors.address}</FormErrorMessage>
                      )}
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* Brand Assets */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <Heading size={{ base: "xs", md: "sm" }} color="black">Brand Assets</Heading>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                        Brand Logo
                      </FormLabel>
                      <Input
                        type="file"
                        size="sm"
                        accept=".jpg,.jpeg,.png,.svg"
                        onChange={(e) => handleFileUpload('brandLogo', e)}
                        bg="white"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Accepted formats: JPG, PNG, SVG
                      </Text>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* Bank Details */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <VStack align="flex-start" spacing={1}>
                    <Heading size={{ base: "xs", md: "sm" }} color="black">Bank Details</Heading>
                    <Text fontSize="xs" color="gray.600" fontWeight="bold">
                      For the UAE: Bank Name, Account Holder Name and IBAN is mandatory
                    </Text>
                  </VStack>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.bankName}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Bank Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.bankName}
                          onChange={(e) => handleFormChange('bankName', e.target.value)}
                          placeholder="Enter bank name"
                          bg="white"
                          borderColor={errors.bankName ? "red.500" : "gray.300"}
                        />
                        {errors.bankName && (
                          <FormErrorMessage fontSize="xs">{errors.bankName}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Account Number
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.accountNumber}
                          onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                          placeholder="Enter account number"
                          bg="white"
                        />
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.accountHolderName}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Account Holder Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.accountHolderName}
                          onChange={(e) => handleFormChange('accountHolderName', e.target.value)}
                          placeholder="Enter account holder name"
                          bg="white"
                          borderColor={errors.accountHolderName ? "red.500" : "gray.300"}
                        />
                        {errors.accountHolderName && (
                          <FormErrorMessage fontSize="xs">{errors.accountHolderName}</FormErrorMessage>
                        )}
                      </FormControl>
                    </HStack>

                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "300px" }} isInvalid={!!errors.iban}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          IBAN <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.iban}
                          onChange={(e) => handleFormChange('iban', e.target.value)}
                          placeholder="Enter IBAN"
                          bg="white"
                          borderColor={errors.iban ? "red.500" : "gray.300"}
                        />
                        {errors.iban && (
                          <FormErrorMessage fontSize="xs">{errors.iban}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl maxW={{ base: "100%", md: "300px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          SWIFT Code
                        </FormLabel>
                        <Input
                          size="sm"
                          value={formData.swiftCode}
                          onChange={(e) => handleFormChange('swiftCode', e.target.value)}
                          placeholder="Enter SWIFT code"
                          bg="white"
                        />
                      </FormControl>
                    </HStack>

                    <HStack spacing={3} wrap="wrap" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                      <FormControl maxW={{ base: "100%", md: "300px" }}>
                        <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                          Bank Account Currency
                        </FormLabel>
                        <Select
                          size="sm"
                          value={formData.bankAccountCurrency}
                          onChange={(e) => handleFormChange('bankAccountCurrency', e.target.value)}
                          bg="white"
                        >
                          <option value="AED">AED</option>
                          <option value="USD">USD</option>
                          <option value="Other">Other (please specify)</option>
                        </Select>
                      </FormControl>

                      {formData.bankAccountCurrency === 'Other' && (
                        <FormControl maxW={{ base: "100%", md: "300px" }}>
                          <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                            Specify Currency
                          </FormLabel>
                          <Input
                            size="sm"
                            value={formData.bankAccountCurrencyOther}
                            onChange={(e) => handleFormChange('bankAccountCurrencyOther', e.target.value)}
                            placeholder="Enter currency code (e.g., EUR, GBP)"
                            bg="white"
                          />
                        </FormControl>
                      )}
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Action Buttons */}
              <HStack spacing={3} mb={4} wrap="wrap" justifyContent="flex-end" w="full">
                <Button
                  leftIcon={<FaUndo />}
                  bg="red.500"
                  color="white"
                  size={{ base: "xs", md: "sm" }}
                  onClick={resetForm}
                  w={{ base: "100%", md: "auto" }}
                  isDisabled={isSaving}
                  _hover={{ bg: "red.600" }}
                >
                  Reset
                </Button>
                <Button
                  leftIcon={<FaSave />}
                  bg="black"
                  color="white"
                  size={{ base: "xs", md: "sm" }}
                  onClick={saveToGoogleSheets}
                  w={{ base: "100%", md: "auto" }}
                  isLoading={isSaving}
                  loadingText="Saving..."
                  isDisabled={isSaving}
                  _hover={{ bg: "gray.800" }}
                >
                  Save
                </Button>
              </HStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </ChakraProvider>
  );
};

export default BrandOnboardingForm;
